import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { FileText, Pencil, Plus, Trash2, Loader2, Info } from "lucide-react";
import { Button } from "@/gestionale/components/ui/button";
import { Input } from "@/gestionale/components/ui/input";
import { Label } from "@/gestionale/components/ui/label";
import { Switch } from "@/gestionale/components/ui/switch";
import { Textarea } from "@/gestionale/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/gestionale/components/ui/table";
import { Badge } from "@/gestionale/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/gestionale/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/gestionale/components/ui/alert-dialog";

interface ModelloDocumento {
  id: string;
  tipo_documento: string;
  titolo: string;
  contenuto: string;
  attivo: boolean;
  created_at: string;
}

const PLACEHOLDERS = [
  { tag: "{nome}", desc: "Nome dell'atleta" },
  { tag: "{cognome}", desc: "Cognome dell'atleta" },
  { tag: "{codice_fiscale}", desc: "Codice fiscale" },
  { tag: "{data_nascita}", desc: "Data di nascita" },
  { tag: "{indirizzo}", desc: "Indirizzo di residenza" },
  { tag: "{certificato_medico}", desc: "Scadenza certificato medico" },
];

export default function DocumentiSettingsCard() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ModelloDocumento | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState("");
  const [titolo, setTitolo] = useState("");
  const [contenuto, setContenuto] = useState("");
  const [attivo, setAttivo] = useState(true);

  const { data: modelli = [], isLoading } = useQuery({
    queryKey: ["modelli-documento"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("modelli_documento")
        .select("*")
        .order("created_at");
      if (error) throw error;
      return data as ModelloDocumento[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        const { error } = await supabase
          .from("modelli_documento")
          .update({ titolo, contenuto, attivo, updated_at: new Date().toISOString() })
          .eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("modelli_documento")
          .insert({ tipo_documento: tipoDocumento, titolo, contenuto, attivo });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modelli-documento"] });
      toast.success(editing ? "Modello aggiornato" : "Modello creato");
      closeDialog();
    },
    onError: (e) => toast.error("Errore: " + e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("modelli_documento").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["modelli-documento"] });
      toast.success("Modello eliminato");
    },
    onError: (e) => toast.error("Errore: " + e.message),
  });

  const openCreate = () => {
    setEditing(null);
    setTipoDocumento("");
    setTitolo("");
    setContenuto("");
    setAttivo(true);
    setDialogOpen(true);
  };

  const openEdit = (m: ModelloDocumento) => {
    setEditing(m);
    setTipoDocumento(m.tipo_documento);
    setTitolo(m.titolo);
    setContenuto(m.contenuto);
    setAttivo(m.attivo);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const insertPlaceholder = (tag: string) => {
    setContenuto((prev) => prev + tag);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Modelli Documento
              </CardTitle>
              <CardDescription>
                Gestisci i modelli dei documenti stampabili e firmabili.
              </CardDescription>
            </div>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> Nuovo Modello
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : modelli.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              Nessun modello documento. Clicca "Nuovo Modello" per crearne uno.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {modelli.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="font-medium">{m.tipo_documento}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{m.titolo}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={m.attivo ? "bg-green-500/15 text-green-700 border-green-500/30" : "bg-muted text-muted-foreground"}
                      >
                        {m.attivo ? "Attivo" : "Disattivato"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(m)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Eliminare il modello?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Il modello "{m.tipo_documento}" verrà eliminato definitivamente.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annulla</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteMutation.mutate(m.id)}>Elimina</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Modello" : "Nuovo Modello Documento"}</DialogTitle>
            <DialogDescription>
              {editing ? "Modifica il contenuto del documento." : "Crea un nuovo modello di documento stampabile."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editing && (
              <div className="space-y-2">
                <Label>Tipo documento *</Label>
                <Input
                  value={tipoDocumento}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  placeholder="Es. Modulo iscrizione"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={titolo}
                onChange={(e) => setTitolo(e.target.value)}
                placeholder="Titolo del documento"
              />
            </div>
            <div className="space-y-2">
              <Label>Contenuto *</Label>
              <Textarea
                value={contenuto}
                onChange={(e) => setContenuto(e.target.value)}
                placeholder="Scrivi il contenuto del documento..."
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                Segnaposto disponibili (clicca per inserire):
              </div>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map((p) => (
                  <Button
                    key={p.tag}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs font-mono"
                    onClick={() => insertPlaceholder(p.tag)}
                    title={p.desc}
                  >
                    {p.tag}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                I segnaposto verranno sostituiti con i dati dell'atleta al momento della stampa.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={attivo} onCheckedChange={setAttivo} />
              <Label>Modello attivo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Annulla</Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !titolo.trim() || !contenuto.trim() || (!editing && !tipoDocumento.trim())}
            >
              {saveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editing ? "Aggiorna" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
